import { useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { createViewHomepage } from "@/ui/views";
import { routerPaths, useOpenFileDetail } from "@/ui/router";
import type {
    TApiBusinessPartnerType,
    TApiHomepageBoard,
    TApiHomepageColumn,
    TApiHomepageRow,
} from "@/apis/proda_finance";
import type { THomepageTypeFilter } from "@/ui/organisms";
import type { TCompositionDeps } from "@/ui/compositionRoot/types";
import type { TSearchableOption } from "@/ui/molecules";
import type {
    THomepageBoard,
    THomepageColumn,
    THomepageColumnKey,
    THomepageRow,
    THomepageView,
} from "@/store";

/** One page of rows per column — matches the server's default cap, and the step
 * by which "Show more" raises it. */
const DEFAULT_ROW_LIMIT = 50;

/** Columns whose rows deep-link to the upload page, preselected (design §7.5). */
const UPLOAD_DEEP_LINK_COLUMNS = new Set<THomepageColumnKey>(["not_yet_due", "upload_now"]);

/** Columns whose rows open the file review page (design §5.3, §7.5). The
 * Accountant "Submitted to (Data) Platform" column opens it read-only — that is the review
 * page's own behaviour for a completed file, not a separate target. "Submitted"
 * (the SP view's third column) is included because an Admin viewing the SP
 * board is server-marked clickable there via `files_all_open`; a row the server
 * marked unclickable never reaches this code.
 */
const REVIEW_DEEP_LINK_COLUMNS = new Set<THomepageColumnKey>([
    "ready_for_you",
    "submitted_to_erp",
    "submitted",
]);

function mapRow(r: TApiHomepageRow): THomepageRow {
    return {
        dealId: r.deal_id,
        dealName: r.deal_name,
        serviceProviderName: r.service_provider_name,
        portfolioCompanyName: r.portfolio_company_name,
        dealFileType: r.deal_file_type,
        fileType: r.file_type,
        period: r.period,
        dueDate: r.due_date,
        daysUntilDue: r.days_until_due,
        uploadAttempts: r.upload_attempts,
        failedAttempts: r.failed_attempts,
        status: r.status,
        submittedAt: r.submitted_at,
        submittedByName: r.submitted_by_name,
        submittedOnTime: r.submitted_on_time,
        daysSinceSubmitted: r.days_since_submitted,
        fileId: r.file_id,
        completedAt: r.completed_at,
        nextAction: r.next_action,
        nextActionLabel: r.next_action_label,
        errorCount: r.error_count,
        isClickable: r.is_clickable,
    };
}

function mapColumn(c: TApiHomepageColumn): THomepageColumn {
    return {
        key: c.key,
        label: c.label,
        count: c.count,
        totalCount: c.total_count,
        rows: c.rows.map(mapRow),
    };
}

function mapBoard(b: TApiHomepageBoard): THomepageBoard {
    return {
        view: b.view,
        selectedPeriods: b.selected_periods,
        availablePeriods: b.available_periods,
        serverDate: b.server_date,
        columns: b.columns.map(mapColumn),
    };
}

export function wireHomepage({ store, useAppStore, api }: TCompositionDeps) {
    const ViewHomepage = createViewHomepage({
        useCases: () => {
            const navigate = useNavigate();
            const openFileDetail = useOpenFileDetail();
            const effectiveCompanyId = useAppStore((state) => state.effectiveCompanyId);
            const boardState = useAppStore((state) => state.homepageBoardState);
            const filters = useAppStore((state) => state.homepageFilters);
            const currentUser = useAppStore((state) => state.user);
            const canFilterAll = currentUser?.permissions.includes("homepage_view_all") ?? false;

            // Every fetch path — mount, period change, filter change — goes
            // through loadBoard, so none can race another's response into a
            // stale state: the abort controller cancels a superseded in-flight
            // request rather than letting both resolve.
            const controllerRef = useRef<AbortController | null>(null);

            /** Fetch the board for the filter state currently in the store.
             *
             * Reading the filters here rather than taking them as arguments
             * keeps every caller a two-liner — set the filter, reload — and
             * means a filter added later (tickets 11-13) needs no change to
             * this function or to the callers that don't touch it.
             */
            const loadBoard = useCallback(() => {
                controllerRef.current?.abort();
                const controller = new AbortController();
                controllerRef.current = controller;

                const current = store.getState().homepageFilters;
                store.getState().setHomepageBoardLoading();
                api.homepage
                    .getBoard(
                        {
                            // Only honoured for an unscoped (admin) caller; the
                            // endpoint derives a scoped caller's view from their
                            // subgroup and ignores this (§6).
                            view: current.view ?? undefined,
                            periods: current.periods.length > 0 ? current.periods : undefined,
                            deal_id: current.dealId ?? undefined,
                            business_partner_id: current.businessPartnerId ?? undefined,
                            business_partner_type:
                                (current.businessPartnerType as TApiBusinessPartnerType | null) ??
                                undefined,
                            finance_user_id: current.financeUserId ?? undefined,
                            row_limit: current.rowLimit ?? undefined,
                        },
                        controller.signal,
                    )
                    .then((response) => {
                        store.getState().setHomepageBoard(mapBoard(response));
                        store.getState().setHomepageFilters({ periods: response.selected_periods });
                    })
                    .catch((error) => {
                        if (error instanceof DOMException && error.name === "AbortError") {
                            return;
                        }
                        store.getState().setHomepageBoardError(error);
                    });
                // eslint-disable-next-line react-hooks/exhaustive-deps
            }, [effectiveCompanyId]);

            const onMount = useCallback(() => {
                loadBoard();
            }, [loadBoard]);

            const onUnmount = useCallback(() => {
                controllerRef.current?.abort();
            }, []);

            /** Reveal the rows a capped column is hiding.
             *
             * Raises the shared page size by one page and refetches. The cap is
             * about how much the board renders at once, not a database page, so
             * there is no cursor to carry — asking for more simply returns more
             * of the same deterministic ordering.
             */
            const onShowMore = useCallback(() => {
                const current = store.getState().homepageFilters.rowLimit ?? DEFAULT_ROW_LIMIT;
                store.getState().setHomepageFilters({ rowLimit: current + DEFAULT_ROW_LIMIT });
                loadBoard();
            }, [loadBoard]);

            /** Any filter change starts the board over at one page: the rows a
             * previous "Show more" revealed belonged to a different question. */
            const onPeriodsChange = useCallback(
                (periods: string[]) => {
                    store.getState().setHomepageFilters({ periods, rowLimit: null });
                    loadBoard();
                },
                [loadBoard],
            );

            /** Submission dropdown options — the caller's own deals, scoped
             * server-side (own-scope callers only ever see deals they are
             * associated with).
             *
             * Narrowed further by the Business Partner filter when one is set,
             * so picking a service provider and then opening Submissions offers
             * only that partner's deals rather than the caller's whole list.
             * `business_partner_id` matches either side of a deal here exactly
             * as it does on the board.
             */
            const onSearchSubmissions = useCallback(
                async (query: string): Promise<TSearchableOption[]> => {
                    const response = await api.deals.list({
                        name: query || undefined,
                        business_partner_id:
                            store.getState().homepageFilters.businessPartnerId ?? undefined,
                        is_active: true,
                        limit: 50,
                    });
                    return response.items.map((deal) => ({ value: deal.id, label: deal.name }));
                },
                // eslint-disable-next-line react-hooks/exhaustive-deps
                [effectiveCompanyId],
            );

            const onSubmissionChange = useCallback(
                (option: TSearchableOption | null) => {
                    store.getState().setHomepageFilters({
                        dealId: option?.value ?? null,
                        dealLabel: option?.label ?? null,
                        rowLimit: null,
                    });
                    loadBoard();
                },
                [loadBoard],
            );

            /** Business Partner dropdown options — Service Providers *and*
             * Portfolio Companies in one list, since the board's
             * `business_partner_id` filter matches either side of a deal. Like
             * the submission list, own-scope callers get this server-scoped to
             * their own business partners.
             */
            const onSearchBusinessPartners = useCallback(
                async (query: string): Promise<TSearchableOption[]> => {
                    const response = await api.businessPartners.list({
                        name: query || undefined,
                        is_active: true,
                        limit: 50,
                    });
                    return response.items.map((bp) => ({ value: bp.id, label: bp.name }));
                },
                // eslint-disable-next-line react-hooks/exhaustive-deps
                [effectiveCompanyId],
            );

            const onBusinessPartnerChange = useCallback(
                (option: TSearchableOption | null) => {
                    store.getState().setHomepageFilters({
                        rowLimit: null,
                        businessPartnerId: option?.value ?? null,
                        businessPartnerLabel: option?.label ?? null,
                        // Changing partner drops any submission filter: the
                        // selected deal usually belongs to the *previous*
                        // partner, and the two together would ask the board for
                        // an impossible combination and render an empty board
                        // with no visible reason why.
                        dealId: null,
                        dealLabel: null,
                    });
                    loadBoard();
                },
                [loadBoard],
            );

            /** Finance-team-member dropdown options (Admin only, §4.3's
             * `finance_user_id`). `finance_team: true` asks the users endpoint
             * for staff rather than business-partner employees, so the list
             * matches what `deal_finance_users` membership can contain. */
            const onSearchFinanceUsers = useCallback(
                async (query: string): Promise<TSearchableOption[]> => {
                    const response = await api.users.list({
                        search: query || undefined,
                        finance_team: true,
                        is_active: true,
                        page_size: 50,
                    });
                    return response.items.map((user) => ({
                        value: user.id,
                        label: user.display_name || user.email,
                    }));
                },
                // eslint-disable-next-line react-hooks/exhaustive-deps
                [effectiveCompanyId],
            );

            const onFinanceUserChange = useCallback(
                (option: TSearchableOption | null) => {
                    store.getState().setHomepageFilters({
                        financeUserId: option?.value ?? null,
                        financeUserLabel: option?.label ?? null,
                        rowLimit: null,
                    });
                    loadBoard();
                },
                [loadBoard],
            );

            /** Admin "Board view" toggle (§7.1, decision log §11): which column
             * set to render. Deliberately *not* the sidebar's "View as" — that
             * emulates a whole role via `role_emulate` and the
             * `X-Effective-Subgroup` header; this only picks a column set, and
             * leaves the caller's scope and permissions untouched.
             *
             * Filters survive the switch: a partner or submission narrowing is
             * equally meaningful in either column set.
             */
            const onBoardViewChange = useCallback(
                (view: THomepageView) => {
                    store.getState().setHomepageFilters({ view, rowLimit: null });
                    loadBoard();
                },
                [loadBoard],
            );

            const onTypeFilterChange = useCallback(
                (value: THomepageTypeFilter) => {
                    store.getState().setHomepageFilters({
                        rowLimit: null,
                        businessPartnerType: value === "all" ? null : value,
                        // The chosen partner may be of the other type, in which
                        // case keeping it would ask for an empty board; same
                        // reasoning as clearing the submission below.
                        businessPartnerId: null,
                        businessPartnerLabel: null,
                        dealId: null,
                        dealLabel: null,
                    });
                    loadBoard();
                },
                [loadBoard],
            );

            const onRowClick = useCallback(
                (row: THomepageRow, columnKey: THomepageColumnKey) => {
                    if (REVIEW_DEEP_LINK_COLUMNS.has(columnKey)) {
                        // No file id means nothing to open — a satisfied row
                        // always has one, so this is the defensive branch, not
                        // an expected state.
                        if (row.fileId) openFileDetail(row.fileId);
                        return;
                    }
                    if (!UPLOAD_DEEP_LINK_COLUMNS.has(columnKey)) return;
                    const params = new URLSearchParams({
                        dealId: row.dealId,
                        period: row.period,
                        fileType: row.fileType,
                    });
                    navigate(`${routerPaths.upload.tbgl}?${params.toString()}`);
                },
                [navigate, openFileDetail],
            );

            return {
                onMount,
                onUnmount,
                useBoard: () => boardState,
                useSubmissionFilter: () =>
                    filters.dealId !== null
                        ? { value: filters.dealId, label: filters.dealLabel ?? "" }
                        : null,
                onSearchSubmissions,
                onSubmissionChange,
                useBusinessPartnerFilter: () =>
                    filters.businessPartnerId !== null
                        ? {
                              value: filters.businessPartnerId,
                              label: filters.businessPartnerLabel ?? "",
                          }
                        : null,
                onSearchBusinessPartners,
                onBusinessPartnerChange,
                // Admin tier only: `homepage_view_all` is the action that makes
                // a board unscoped, which is exactly the condition under which
                // these filters have anything to narrow (§6).
                useCanFilterAll: () => canFilterAll,
                // Falls back to whichever column set the server actually sent,
                // so the toggle reflects reality before the first explicit
                // switch rather than guessing a default.
                // Reads the filter state, not the last board response, so a
                // ticked month registers immediately instead of snapping back
                // until the refetch lands. The server still has the last word:
                // it normalises the selection and loadBoard writes that back
                // into the same filter field when the response arrives.
                onShowMore,
                useIsLoadingMore: () => boardState.status === "pending" && boardState.data !== null,
                useSelectedPeriods: () =>
                    filters.periods.length > 0
                        ? filters.periods
                        : (boardState.data?.selectedPeriods ?? []),
                useBoardView: () => filters.view ?? boardState.data?.view ?? "accountant",
                onBoardViewChange,
                useTypeFilter: () =>
                    (filters.businessPartnerType as THomepageTypeFilter | null) ?? "all",
                onTypeFilterChange,
                useFinanceUserFilter: () =>
                    filters.financeUserId !== null
                        ? { value: filters.financeUserId, label: filters.financeUserLabel ?? "" }
                        : null,
                onSearchFinanceUsers,
                onFinanceUserChange,
                onPeriodsChange,
                onRowClick,
            };
        },
    });

    return { ViewHomepage };
}
